import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../lib/api';
import PhaseBadge, { PhaseKey } from './PhaseBadge';

const PHASE_BORDER_COLOR: Partial<Record<PhaseKey, string>> = {
  phase_0_bootstrap:       'var(--border)',
  phase_1_requirements:    'var(--primary)',
  phase_2_architecture:    'var(--accent)',
  phase_2_5_ux_design:     'var(--accent-2, var(--accent))',
  phase_3_sprint_planning: 'var(--warning)',
  phase_4_development:     'var(--success)',
  phase_5_code_review:     'var(--accent-warm)',
  phase_6_testing:         'var(--error)',
  phase_7_deployment:      'var(--text-secondary)',
  phase_8_retrospective:   'var(--text-secondary)',
};

export interface Project {
  id: string;
  name: string;
  productArea: string | null;
  currentPhase: PhaseKey;
  status?: string;
  archivedAt?: string | null;
  deletedAt?: string | null;
  createdAt: string;
  updatedAt: string;
  // Sprint 12.5 ACC.C1 (#421) — Fast Track project type. Standard projects
  // omit this or set 'standard'; 'fast_track' renders the amber FT badge
  // and signals that Phases 1+2 are bypassed.
  projectType?: 'standard' | 'fast_track';
}

interface GateSummary {
  hasFailures: boolean;
  failureCount: number;
  gateType: string | null;
  phase: string | null;
}

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatRelativeTime(dateString: string): string {
  const now = Date.now();
  const then = new Date(dateString).getTime();
  const diffMs = now - then;
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSeconds < 60) return 'just now';
  if (diffMinutes < 60) return `${diffMinutes}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 30) return `${diffDays}d ago`;
  return formatDate(dateString);
}

export default function ProjectCard({ project, onRestore }: { project: Project; onRestore?: () => void }) {
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const [nameValue, setNameValue] = useState(project.name);
  const [displayName, setDisplayName] = useState(project.name);
  const [gateSummary, setGateSummary] = useState<GateSummary | null>(null);
  const [restoring, setRestoring] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const isInactive = project.status === 'ARCHIVED' || project.status === 'DELETED';

  const handleRestore = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setRestoring(true);
    try {
      await apiFetch(`/projects/${project.id}/restore`, { method: 'PATCH' });
      onRestore?.();
    } catch {
      // silently fail
    } finally {
      setRestoring(false);
    }
  };

  useEffect(() => {
    if (editing) inputRef.current?.select();
  }, [editing]);

  useEffect(() => {
    apiFetch<GateSummary>(`/projects/${project.id}/gate-summary`)
      .then(setGateSummary)
      .catch(() => {});
  }, [project.id]);

  const startEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    setNameValue(displayName);
    setEditing(true);
  };

  const cancelEdit = () => setEditing(false);

  const saveEdit = async () => {
    const trimmed = nameValue.trim();
    if (!trimmed || trimmed === displayName) {
      cancelEdit();
      return;
    }
    try {
      await apiFetch(`/projects/${project.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ name: trimmed }),
      });
      setDisplayName(trimmed);
    } catch {
      // silently revert on error
    } finally {
      setEditing(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') { e.preventDefault(); void saveEdit(); }
    if (e.key === 'Escape') { e.preventDefault(); cancelEdit(); }
  };

  const borderColor = PHASE_BORDER_COLOR[project.currentPhase] ?? 'var(--border)';

  return (
    <div
      onClick={() => !editing && navigate(`/projects/${project.id}`)}
      className="group transition-all"
      style={{
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-lg)',
        borderTop: `3px solid ${borderColor}`,
        padding: 'var(--space-5)',
        boxShadow: 'var(--shadow-sm)',
        cursor: editing ? 'default' : 'pointer',
        transform: 'translateY(0)',
        transition: 'box-shadow 200ms, border-color 200ms, transform 200ms',
      }}
      onMouseEnter={e => { if (!editing) { (e.currentTarget as HTMLDivElement).style.boxShadow = 'var(--shadow-md)'; (e.currentTarget as HTMLDivElement).style.transform = 'translateY(-1px)'; } }}
      onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.boxShadow = 'var(--shadow-sm)'; (e.currentTarget as HTMLDivElement).style.transform = 'translateY(0)'; }}
    >
      <div className="flex items-start justify-between gap-3 mb-2">
        {editing ? (
          <div className="flex items-center gap-1 min-w-0 flex-1" onClick={e => e.stopPropagation()}>
            <input
              ref={inputRef}
              value={nameValue}
              onChange={e => setNameValue(e.target.value)}
              onKeyDown={handleKeyDown}
              onBlur={cancelEdit}
              maxLength={100}
              style={{ fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 700, color: 'var(--text)', borderBottom: '1px solid var(--primary)', outline: 'none', background: 'transparent', width: '100%' }}
            />
            <button
              onMouseDown={e => { e.preventDefault(); e.stopPropagation(); void saveEdit(); }}
              style={{ color: 'var(--primary)', fontSize: 14, fontWeight: 700, flexShrink: 0, padding: '0 2px' }}
              aria-label="Save project name"
            >
              ✓
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-1.5 min-w-0 flex-1">
            <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 700, color: 'var(--text)', lineHeight: 1.35 }} className="truncate">{displayName}</h3>
            <button
              onClick={startEdit}
              className="opacity-0 group-hover:opacity-100 flex-shrink-0 transition-opacity p-0.5 rounded"
              style={{ color: 'var(--text-secondary)' }}
              aria-label="Edit project name"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
              </svg>
            </button>
          </div>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {/* ACC.C1 (#421) — amber FT badge. Distinct from PhaseBadge so
              both can appear: phase tells you where the project is now,
              FT tells you what KIND of project it is. The badge stays
              on for the whole project lifecycle. */}
          {project.projectType === 'fast_track' && (
            <span
              data-testid="ft-badge"
              title="Fast Track project — Phases 1 and 2 bypassed by design"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                padding: '2px 6px',
                borderRadius: 4,
                background: 'rgba(217,119,6,0.10)',
                border: '1px solid rgba(217,119,6,0.4)',
                fontFamily: 'var(--font-display)',
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: '0.05em',
                color: 'var(--accent-amber, #d97706)',
              }}
            >
              FT
            </span>
          )}
          <PhaseBadge phase={project.currentPhase} />
        </div>
      </div>
      <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)', marginBottom: 12 }}>
        {project.productArea ?? 'No product area'}
      </p>
      <div className="flex items-center justify-between">
        <p style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)' }}>Created {formatDate(project.createdAt)}</p>
        <div className="flex items-center gap-3">
          {isInactive && (
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              padding: '2px 8px', borderRadius: 12,
              background: project.status === 'DELETED' ? 'rgba(239,68,68,0.10)' : 'rgba(120,113,108,0.10)',
              color: project.status === 'DELETED' ? 'var(--error)' : 'var(--text-secondary)',
              fontSize: 11, fontWeight: 600, fontFamily: 'var(--font-body)',
            }}>
              {project.status === 'DELETED' ? 'Deleted' : 'Archived'}
            </span>
          )}
          {isInactive && (
            <button
              onClick={handleRestore}
              disabled={restoring}
              style={{
                fontSize: 12, fontWeight: 600, fontFamily: 'var(--font-body)',
                color: 'var(--primary)', background: 'none', border: 'none',
                cursor: restoring ? 'not-allowed' : 'pointer',
                padding: 0, textDecoration: 'underline',
                opacity: restoring ? 0.6 : 1,
              }}
            >
              {restoring ? 'Restoring...' : 'Restore'}
            </button>
          )}
          {!isInactive && gateSummary?.hasFailures && (
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              padding: '2px 8px', borderRadius: 12,
              background: 'rgba(245,158,11,0.10)', color: '#d97706',
              fontSize: 11, fontWeight: 600, fontFamily: 'var(--font-body)',
            }}>
              ⚠ {gateSummary.failureCount} gate issue{gateSummary.failureCount !== 1 ? 's' : ''}
            </span>
          )}
          {project.updatedAt && (
            <p style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)', margin: 0 }}>Last active {formatRelativeTime(project.updatedAt)}</p>
          )}
        </div>
      </div>
    </div>
  );
}
